import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DurationWheelPicker } from '@/components/DurationWheelPicker';
import {
  ENDURANCE_PRESETS,
  RACE_PRESETS,
  STRENGTH_PRESETS,
  formatGoalClock,
  parseClockToMinutes,
  presetIdForDistanceKm,
  remainingGap,
  type GoalType,
  type RacePresetId,
} from '@/lib/specificGoals';
import { Calendar, Dumbbell, Footprints, Scale, Timer } from 'lucide-react';

export type SpecificGoalDraft = {
  goal_type: GoalType | string;
  exercise_name: string;
  target_value: number;
  target_unit: string;
  current_value: number;
  starting_value: number | null;
  target_date: string | null;
  is_active: boolean;
  description: string;
  /** UI-only. Keeps Custom selected even if the typed distance matches a 5K/10K/etc. */
  racePresetId?: RacePresetId;
};

const PRESET_RACE_NAMES: Set<string> = new Set<string>(
  RACE_PRESETS.filter((p) => p.id !== 'custom').map((p) => p.name),
);

type Props = {
  value: SpecificGoalDraft;
  onChange: (next: SpecificGoalDraft) => void;
  distanceUnit: 'km' | 'miles';
  weightUnit: 'kg' | 'lbs';
};

const TYPES: { id: GoalType; labelKey: string; fallback: string; icon: typeof Footprints }[] = [
  { id: 'running', labelKey: 'components.goalCard.goalTypeRunning', fallback: 'Running', icon: Footprints },
  { id: 'strength', labelKey: 'components.goalCard.goalTypeStrength', fallback: 'Strength', icon: Dumbbell },
  { id: 'endurance', labelKey: 'components.goalCard.goalTypeEndurance', fallback: 'Endurance', icon: Timer },
  { id: 'weight', labelKey: 'components.goalCard.goalTypeWeight', fallback: 'Weight', icon: Scale },
];

export function SpecificGoalForm({ value, onChange, distanceUnit, weightUnit }: Props) {
  const { t } = useTranslation();
  const type = value.goal_type;
  const raceId: RacePresetId =
    value.racePresetId ??
    presetIdForDistanceKm(value.target_value, value.exercise_name, value.target_unit);
  const isCustomRace = type === 'running' && raceId === 'custom';
  const dateRef = useRef<HTMLInputElement>(null);
  const targetMinutes = parseFloat(String(value.description || '0')) || 0;

  useEffect(() => {
    if (type === 'running' && !value.target_unit) {
      onChange({ ...value, target_unit: distanceUnit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, distanceUnit]);

  const setType = (next: GoalType) => {
    onChange({
      ...value,
      goal_type: next,
      exercise_name: '',
      target_value: 0,
      current_value: 0,
      description: '',
      racePresetId: next === 'running' ? 'custom' : undefined,
      target_unit:
        next === 'running' ? distanceUnit : next === 'endurance' ? 'reps' : weightUnit,
    });
  };

  const setRace = (id: RacePresetId) => {
    const preset = RACE_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    if (id === 'custom') {
      const keepName =
        value.exercise_name.trim() && !PRESET_RACE_NAMES.has(value.exercise_name.trim());
      onChange({
        ...value,
        racePresetId: 'custom',
        exercise_name: keepName ? value.exercise_name : '',
        target_value: 0,
        target_unit: distanceUnit,
        description: '',
        current_value: 0,
      });
      return;
    }
    const km = preset.km;
    const target = distanceUnit === 'miles' ? Math.round((km / 1.609344) * 100) / 100 : km;
    onChange({
      ...value,
      racePresetId: id,
      exercise_name: preset.name,
      target_value: target,
      target_unit: distanceUnit,
      description: String(preset.defaultMinutes),
    });
  };

  const gapPreview =
    type === 'running' && targetMinutes > 0 && value.current_value > 0
      ? remainingGap({
          current: value.current_value,
          target: targetMinutes,
          lowerIsBetter: true,
          isTime: true,
        })
      : type === 'strength' && value.target_value > 0 && value.current_value > 0
        ? remainingGap({
            current: value.current_value,
            target: value.target_value,
            lowerIsBetter: false,
            unit: value.target_unit,
          })
        : type === 'endurance' && value.target_value > 0 && value.current_value > 0
          ? remainingGap({
              current: value.current_value,
              target: value.target_value,
              lowerIsBetter: false,
              unit: value.target_unit,
              isTime: value.target_unit === 'minutes',
            })
          : type === 'weight' && value.target_value > 0 && value.current_value > 0
            ? remainingGap({
                current: value.current_value,
                target: value.target_value,
                lowerIsBetter: value.current_value > value.target_value,
                unit: value.target_unit,
              })
            : null;

  const dateLabel =
    type === 'running'
      ? t('components.goalCard.raceDate', { defaultValue: 'Race day' })
      : t('components.goalCard.deadlineLabel');

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-2">
        {TYPES.map((opt) => {
          const Icon = opt.icon;
          const on = type === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setType(opt.id)}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition-all ${
                on
                  ? 'border-primary bg-primary/15 text-primary'
                  : 'border-border bg-muted/30 text-muted-foreground hover:border-primary/40'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {t(opt.labelKey, { defaultValue: opt.fallback })}
            </button>
          );
        })}
      </div>

      {type === 'running' ? (
        <>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              {t('components.goalCard.raceDistance', { defaultValue: 'Distance' })}
            </Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {RACE_PRESETS.filter((p) => p.id !== 'custom').map((p) => {
                const on = raceId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setRace(p.id)}
                    className={`rounded-xl border px-2 py-2 text-xs font-semibold transition-all ${
                      on
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setRace('custom')}
              className={`mt-2 w-full rounded-xl border px-2 py-2.5 text-xs font-semibold transition-all ${
                isCustomRace
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              {t('components.goalCard.customRace', { defaultValue: 'Custom / named race' })}
            </button>
            {isCustomRace && (
              <p className="text-[11px] text-muted-foreground mt-1.5">
                {t('components.goalCard.customRaceHint', {
                  defaultValue: 'Set your own distance, race name, goal time, and current best.',
                })}
              </p>
            )}
          </div>

          <div>
            <Label>{t('components.goalCard.raceName', { defaultValue: 'Race name' })}</Label>
            <Input
              value={value.exercise_name}
              onChange={(e) =>
                onChange({
                  ...value,
                  exercise_name: e.target.value,
                  racePresetId: isCustomRace ? 'custom' : value.racePresetId,
                })
              }
              placeholder={
                isCustomRace
                  ? t('components.goalCard.customRaceNamePlaceholder', {
                      defaultValue: 'e.g. Office 8K, trail ultra, birthday run',
                    })
                  : t('components.goalCard.raceNamePlaceholder', {
                      defaultValue: 'e.g. Stockholm Marathon',
                    })
              }
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>

          {isCustomRace && (
            <div>
              <Label>
                {t('components.goalCard.distanceLabel', {
                  defaultValue: 'Distance',
                })}{' '}
                ({distanceUnit})
              </Label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0.1"
                value={value.target_value || ''}
                onChange={(e) =>
                  onChange({
                    ...value,
                    racePresetId: 'custom',
                    target_value: parseFloat(e.target.value) || 0,
                    target_unit: distanceUnit,
                  })
                }
                placeholder={distanceUnit === 'miles' ? '8.0' : '8.0'}
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
          )}

          <div>
            <Label>{t('components.goalCard.goalTimeLabel', { defaultValue: 'Goal time' })}</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
              {t('components.goalCard.scrollTimeHint', { defaultValue: 'Scroll the wheels — no typing.' })}
            </p>
            <DurationWheelPicker
              minutes={targetMinutes}
              onChange={(mins) => onChange({ ...value, description: String(mins) })}
            />
          </div>

          <div>
            <Label>{t('components.goalCard.currentBestOptional', { defaultValue: 'Current best (optional)' })}</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
              {t('components.goalCard.currentBestHint', {
                defaultValue: 'Your latest time at this distance. Skip if you do not have one yet.',
              })}
            </p>
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...value,
                  current_value: value.current_value > 0 ? 0 : (targetMinutes || 40),
                })
              }
              className={`mb-2 w-full h-10 rounded-xl border text-sm font-medium ${
                value.current_value > 0
                  ? 'border-primary bg-primary/15 text-primary'
                  : 'border-border text-muted-foreground'
              }`}
            >
              {value.current_value > 0
                ? t('components.goalCard.hasCurrentBest', { defaultValue: 'Using a current best — scroll to set it' })
                : t('components.goalCard.addCurrentBest', { defaultValue: 'I have a current best — tap to add' })}
            </button>
            {value.current_value > 0 && (
              <DurationWheelPicker
                minutes={value.current_value}
                onChange={(mins) => onChange({ ...value, current_value: mins || targetMinutes || 1 })}
              />
            )}
          </div>
        </>
      ) : (
        <>
          <div>
            <Label>
              {type === 'weight'
                ? t('components.goalCard.metricLabel', { defaultValue: 'Metric' })
                : t('components.goalCard.exerciseLabel', { defaultValue: 'Exercise' })}
            </Label>
            {type === 'strength' && (
              <div className="mt-2 flex flex-wrap gap-1.5 mb-2">
                {STRENGTH_PRESETS.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => onChange({ ...value, exercise_name: name, target_unit: weightUnit })}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                      value.exercise_name === name
                        ? 'border-primary bg-primary/15 text-primary'
                        : 'border-border text-muted-foreground'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
            {type === 'endurance' && (
              <div className="mt-2 flex flex-wrap gap-1.5 mb-2">
                {ENDURANCE_PRESETS.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() =>
                      onChange({ ...value, exercise_name: p.name, target_unit: p.unit, current_value: 0, target_value: 0 })
                    }
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                      value.exercise_name === p.name
                        ? 'border-primary bg-primary/15 text-primary'
                        : 'border-border text-muted-foreground'
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
            <Input
              value={value.exercise_name}
              onChange={(e) => onChange({ ...value, exercise_name: e.target.value })}
              placeholder={
                type === 'strength'
                  ? 'Bench Press'
                  : type === 'endurance'
                    ? 'Pull-ups'
                    : t('components.goalCard.weightPlaceholder', { defaultValue: 'Body weight' })
              }
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
          {type === 'endurance' && value.target_unit === 'minutes' ? (
            <div className="space-y-3">
              <div>
                <Label>{t('components.goalCard.currentValueLabel')}</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
                  {t('components.goalCard.scrollTimeHint', { defaultValue: 'Scroll the wheels — no typing.' })}
                </p>
                <DurationWheelPicker
                  minutes={value.current_value || 0}
                  onChange={(mins) => onChange({ ...value, current_value: mins })}
                />
              </div>
              <div>
                <Label>{t('components.goalCard.targetLabel')}</Label>
                <div className="mt-2">
                  <DurationWheelPicker
                    minutes={value.target_value || 0}
                    onChange={(mins) => onChange({ ...value, target_value: mins })}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('components.goalCard.currentValueLabel')}</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={value.current_value || ''}
                  onChange={(e) => onChange({ ...value, current_value: parseFloat(e.target.value) || 0 })}
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
              <div>
                <Label>{t('components.goalCard.targetLabel')}</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={value.target_value || ''}
                  onChange={(e) => onChange({ ...value, target_value: parseFloat(e.target.value) || 0 })}
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
            </div>
          )}
          <div>
            <Label>{t('components.goalCard.unitLabel', { defaultValue: 'Unit' })}</Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(type === 'weight'
                ? ['kg', 'lbs', '%']
                : type === 'endurance'
                  ? ['reps', 'minutes', 'km']
                  : ['kg', 'lbs', 'reps']
              ).map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => onChange({ ...value, target_unit: u })}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                    value.target_unit === u
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div>
        <Label>{dateLabel}</Label>
        <div className="relative mt-1.5">
          <div className="w-full h-11 rounded-xl border border-border bg-card px-3 flex items-center gap-2 pointer-events-none">
            <Calendar className="h-4 w-4 text-primary shrink-0" />
            {value.target_date ? (
              <span className="text-sm font-medium">
                {new Date(`${value.target_date}T12:00:00`).toLocaleDateString()}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">
                {type === 'running'
                  ? t('components.goalCard.raceDatePlaceholder', { defaultValue: 'Tap to choose race day' })
                  : t('components.goalCard.deadlinePlaceholder', { defaultValue: 'Tap to choose a date (optional)' })}
              </span>
            )}
          </div>
          <input
            ref={dateRef}
            type="date"
            value={value.target_date || ''}
            onChange={(e) => onChange({ ...value, target_date: e.target.value || null })}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        {type === 'running' && !value.target_date && (
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('components.goalCard.raceDateHint', { defaultValue: 'Race day keeps you honest — Arnold will count it down with you.' })}
          </p>
        )}
      </div>

      {gapPreview && (
        <div className="rounded-xl border border-primary/25 bg-primary/10 px-3 py-2.5 text-center">
          <p className="text-sm font-semibold text-primary">{gapPreview}</p>
          {type === 'running' && targetMinutes > 0 && value.current_value > 0 && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {formatGoalClock(value.current_value)} → {formatGoalClock(targetMinutes)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function normalizeGoalDraft(draft: SpecificGoalDraft): SpecificGoalDraft {
  const { racePresetId, ...rest } = draft;
  const next: SpecificGoalDraft = { ...rest };
  if (next.goal_type === 'running') {
    if (next.description && next.description.includes(':')) {
      next.description = String(parseClockToMinutes(next.description));
    }
    if (!next.exercise_name.trim() && racePresetId !== 'custom') {
      const inferred = presetIdForDistanceKm(next.target_value, null, next.target_unit);
      const preset = RACE_PRESETS.find((p) => p.id === inferred && p.id !== 'custom');
      next.exercise_name = preset?.name || 'Running race';
    }
    if (!next.target_unit) next.target_unit = 'km';
  }
  next.starting_value = next.current_value;
  return next;
}
